import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useWorkspace } from '@/contexts/WorkspaceContext';

export interface HeatmapFilters {
  customer_id?: string;
  supplier_id?: string;
  account_number?: string;
  months?: 3 | 6 | 12;
  limit?: number;
  offset?: number;
  sort_by?: 'customer_name' | 'supplier_name' | 'account_number' | 'missing_count';
  sort_order?: 'asc' | 'desc';
}

export function useHeatmapData(filters?: HeatmapFilters) {
  const { currentWorkspace } = useWorkspace();
  
  return useQuery({
    queryKey: ['heatmap-data', currentWorkspace?.id, filters],
    queryFn: async () => {
      // Ensure we have a valid session
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error('No active session');
      }

      const { data, error } = await supabase.functions.invoke('analytics-heatmap', {
        body: { 
          action: 'get_heatmap_data', 
          workspace_id: currentWorkspace?.id,
          filters,
          offset: filters?.offset || 0,
          limit: filters?.limit || 12,
          sort_by: filters?.sort_by || 'customer_name',
          sort_order: filters?.sort_order || 'asc'
        },
      });
      if (error) throw error;
      return data;
    },
    enabled: !!currentWorkspace?.id,
  });
}

export function useHeatmapSummary(filters?: HeatmapFilters) {
  const { currentWorkspace } = useWorkspace();
  
  return useQuery({
    queryKey: ['heatmap-summary', currentWorkspace?.id, filters],
    queryFn: async () => {
      // Ensure we have a valid session
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error('No active session');
      }

      const { data, error } = await supabase.functions.invoke('analytics-heatmap', {
        body: { 
          action: 'get_summary',
          workspace_id: currentWorkspace?.id,
          filters: {
            customer_id: filters?.customer_id,
            supplier_id: filters?.supplier_id,
            account_number: filters?.account_number
          }
        },
      });
      if (error) throw error;
      return data;
    },
    enabled: !!currentWorkspace?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

export function useExportHeatmapCSV() {
  const { toast } = useToast();
  const { currentWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: async (filters?: HeatmapFilters) => {
      const response = await fetch(`https://dyftqspuiyabqzjslwyw.supabase.co/functions/v1/analytics-heatmap`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
        body: JSON.stringify({ 
          action: 'export_csv',
          workspace_id: currentWorkspace?.id,
          filters
        }),
      });

      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().split('T')[0];
      link.download = `heatmap-export-${today}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true };
    },
    onSuccess: () => {
      toast({
        title: 'Export complete',
        description: 'Heat map data has been downloaded',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Export failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
